import React, { useState } from 'react';

import { css } from '@emotion/react';
import { ErrorMessage } from '@hookform/error-message';
import { SubmitHandler, useForm } from 'react-hook-form';

import LoadingSpinner from '@components/Common/LoadingSpinner';

import { useSubmitLogin } from '@hooks/api/submit/useSubmitAPIs';
import useQueryString from '@hooks/useQueryString';

import { palette } from '@styles/theme';

import { emailValidateOptions } from '@constants/validation';

interface FormInputs {
  email: string;
}

/** 기업보고서 제출 로그인 */
const SubmitLogin = () => {
  const { key } = useQueryString();
  const [emailFocus, setEmailFocus] = useState<boolean>(false);

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<FormInputs>({});

  /** 로그인 API */
  const { mutate, isLoading } = useSubmitLogin();

  // 사업자등록번호는 로그인에 더 이상 쓰지 않아 이메일만 보낸다
  const onSubmit: SubmitHandler<FormInputs> = async ({ email }) => {
    try {
      mutate({
        key: String(key),
        email,
      });
    } catch (error: any) {
      console.log('LOGIN ERROR : ', error);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div css={container}>
        <div css={wrapper}>
          <div css={logoWrap}>
            <img src={'/assets/images/gms_logo_2.png'} alt="logo" />
          </div>
          <div css={title}>기업보고서 제출 시스템</div>
          <form css={formWrap} onSubmit={handleSubmit(onSubmit)}>
            <div
              css={[inputWrap, emailFocus && inputWrapOnFocus]}
              onFocus={() => setEmailFocus(true)}
              onBlur={() => setEmailFocus(false)}
            >
              <div>이메일주소</div>
              <input
                css={loginInput}
                type="email"
                autoComplete="off"
                placeholder="이메일주소를 입력해 주세요."
                {...register('email', emailValidateOptions)}
              />
            </div>
            {!isValid && (
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => (
                  <p css={errorMessage}>[이메일] {message}</p>
                )}
              />
            )}

            <div css={btnWrap}>
              <button type="submit" onSubmit={handleSubmit(onSubmit)}>
                로그인
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SubmitLogin;

const container = css`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const wrapper = css`
  display: flex;
  flex-direction: column;
  width: 480px;
  height: auto;
  box-shadow: 5px 15px 40px rgba(206, 208, 214, 0.5);
  border-radius: 25px;
  background-color: ${palette.white};
  padding: 60px;
`;

const logoWrap = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 50px;
  img {
    height: 53px;
    object-fit: contain;
  }
`;

const formWrap = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const loginInput = css`
  width: 100%;
  height: 50px;
  border-radius: 5px;
  padding: 0 20px 0 0;
  background-color: ${palette.white};
  display: flex;

  :focus {
    outline: none;
  }

  /* 크롬 자동완성 배경(연파랑)은 UA 스타일이라 background 로 못 덮는다. 흰색 inset 그림자로 가리고 글자색만 맞춘다 */
  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus {
    box-shadow: 0 0 0 1000px ${palette.white} inset;
    -webkit-text-fill-color: ${palette.black};
  }
`;

const inputWrap = css`
  display: flex;
  align-items: center;
  padding-left: 20px;
  border-radius: 5px;
  border: 1px solid ${palette.gray3};
  /* 라벨 폭: '이메일주소' 5글자 + 오른쪽 여백 */
  div {
    font-size: 14px;
    font-weight: 700;
    width: 110px;
    padding-right: 20px;
  }
`;

const inputWrapOnFocus = css`
  border-color: ${palette.primaryColor};
`;

const btnWrap = css`
  // margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  button {
    width: 100%;
    height: 50px;
    border-radius: 5px;
    background-color: ${palette.secondaryColor};
    color: ${palette.white};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
      transition: 0.3s;
    }
  }

  .find {
    background-color: ${palette.white};
    color: ${palette.black};
    &:hover {
      background-color: ${palette.gray2};
    }
  }
`;

const title = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 700;
  color: ${palette.black};
`;

const errorMessage = css`
  color: ${palette.colorDanger};
  font-size: 11px;
  padding: 0 5px;
  line-height: 12px;
`;
